import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { IoAddCircleOutline, IoCloseSharp } from "react-icons/io5";
import { RiDeleteBin5Line } from "react-icons/ri";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { server } from "../../../App";
import Spacer from "../../../assets/images/spacer.png";
import DocumentPreview from "../../../component/DocumentPreview";
import CreateModal from "../../../component/Model/CreateModel";
import {
  useGetVehicleColorQuery,
  useGetVehicleMakeQuery,
  useGetVehicleModelQuery,
  useGetVehicleTypeQuery,
  useGetVehicleYearQuery,
} from "../../../store/api/configurationSlice";
import {
  useAddVehiclesMutation,
  useGetVehicleDetailsQuery,
  useUpdateVehiclesMutation,
} from "../../../store/api/vendor/vehiclesSlice";
import { multiPartData2 } from "../../../utils";
import AddVehicleSkelton from "../../../component/skelton/AddVehicleSkelton";

const initialState = {
  make: "",
  model: "",
  color: "",
  year: "",
  vehicle_type: "",
  license_number: "",
  registration_number: "",
  vehicle_images: [],
  seat: "",
  vin: "",
  current_mileage: "",
  fare_rate: "0",
  description: "",
  vehicle_condition: "",
  tyre_condition: "",
};

const previewInitialState = {
  imagesReview: [],
};

const AddVehicle = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { role } = useParams();

  const [show, setShow] = useState(false);
  const [vehicleInfo, setVehicleInfo] = useState({
    years: [],
    makes: [],
    models: [],
    colors: [],
    types: [],
  });
  const [formData, setFormData] = useState(initialState);
  const [preview, setPreview] = useState(previewInitialState);
  const [errors, setErrors] = useState({});

  // addVehicles query
  const [addVehicles, { isLoading }] = useAddVehiclesMutation();
  // UPDATE Vehicles query
  const [updateVehicles, { isLoading: isUpdateLoading }] =
    useUpdateVehiclesMutation();

  // get queries
  const {
    data: yearsData,
    isSuccess: isSuccessYears,
    isLoading: isYearsLoading,
  } = useGetVehicleYearQuery({});
  const {
    data: makesData,
    isSuccess: isSuccessMakes,
    isLoading: isMakesLoading,
  } = useGetVehicleMakeQuery({});
  const {
    data: modelData,
    isSuccess: isSuccessModel,
    isLoading: isModelLoading,
  } = useGetVehicleModelQuery({ id: formData.make }, { skip: !formData.make });
  const {
    data: colorData,
    isSuccess: isSuccessColor,
    isLoading: isColorLoading,
  } = useGetVehicleColorQuery({});
  const {
    data: typeData,
    isSuccess: isSuccessType,
    isLoading: isTypeLoading,
  } = useGetVehicleTypeQuery({});
  const {
    data: vehicleDetailData,
    isSuccess: isSuccessVehicleDetail,
    isLoading: isVehicleDetailLoading,
  } = useGetVehicleDetailsQuery(id, { skip: !id });

  useEffect(() => {
    if (isSuccessYears && yearsData?.data) {
      setVehicleInfo((prev) => ({ ...prev, years: yearsData.data.years }));
    }
    if (isSuccessMakes && makesData?.data) {
      setVehicleInfo((prev) => ({ ...prev, makes: makesData.data.makes }));
    }
    if (isSuccessModel && modelData?.data) {
      setVehicleInfo((prev) => ({ ...prev, models: modelData.data.models }));
    }
    if (isSuccessColor && colorData?.data) {
      setVehicleInfo((prev) => ({ ...prev, colors: colorData.data.colors }));
    }
    if (isSuccessType && typeData?.data) {
      setVehicleInfo((prev) => ({ ...prev, types: typeData.data.types }));
    }
  }, [
    isSuccessYears,
    yearsData,
    isSuccessMakes,
    makesData,
    isSuccessModel,
    modelData,
    isSuccessColor,
    colorData,
    isSuccessType,
    typeData,
  ]);

  useEffect(() => {
    if (isSuccessVehicleDetail && vehicleDetailData?.data) {
      setFormData({
        make: vehicleDetailData.data?.make?.id || "",
        model: vehicleDetailData.data?.model?.id || "",
        color: vehicleDetailData.data?.color?.id || "",
        year: vehicleDetailData.data?.year?.id || "",
        vehicle_type: vehicleDetailData.data?.vehicle_type?.id || "",
        license_number: vehicleDetailData.data.license_number || "",
        registration_number: vehicleDetailData.data.registration_number || "",
        vehicle_images:
          vehicleDetailData?.data?.vehicle_images?.map((img) => ({
            id: img.id,
            image: img.img,
          })) || [],
        seat: vehicleDetailData.data.seat || "",
        vin: vehicleDetailData.data.vin || "",
        current_mileage: vehicleDetailData.data.current_mileage || "",
        fare_rate: vehicleDetailData.data.fare_rate || "",
        description: vehicleDetailData.data.description || "",
        vehicle_condition: vehicleDetailData.data.vehicle_condition || "",
        tyre_condition: vehicleDetailData.data.tyre_condition || "",
      });

      setPreview((prev) => ({
        ...prev,
        imagesReview: vehicleDetailData?.data?.vehicle_images?.map(
          (img) => `${server}${img.img}`,
        ),
      }));
    }
  }, [vehicleDetailData, isSuccessVehicleDetail]);

  const handleClose = () => {
    setShow(false);
    navigate(`/vehicles`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({
      ...prev,
      [name]: value ? "" : `${t("errors." + name)} ${t("errors.required")}`,
    }));
  };

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const fileHandler = (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

    const validFiles = [];
    const previewURLs = [];
    const oversizedFiles = [];

    files.forEach((file) => {
      if (!allowedTypes.includes(file.type)) return;
      if (file.size > MAX_FILE_SIZE) {
        oversizedFiles.push(file.name);
        return;
      }
      validFiles.push({ image: file });
      previewURLs.push(URL.createObjectURL(file));
    });

    if (oversizedFiles.length > 0) {
      setErrors((prev) => ({
        ...prev,
        vehicle_images: `File size exceeds 5MB limit: ${oversizedFiles.join(", ")}`,
      }));
    }

    if (validFiles.length > 0) {
      setFormData((prev) => ({
        ...prev,
        vehicle_images: [...prev.vehicle_images, ...validFiles],
      }));

      if (oversizedFiles.length === 0) {
        setErrors((prev) => ({ ...prev, vehicle_images: "" }));
      }

      setPreview((prev) => ({
        ...prev,
        imagesReview: [...prev.imagesReview, ...previewURLs],
      }));
    } else if (oversizedFiles.length === 0) {
      toast.error(
        `${t("only")} ${t("these")} ${t("file")} ${t("formats")} ${t(
          "are",
        )} ${t("allowed")}: jpeg, jpg, png`,
        { position: toast.POSITION.TOP_RIGHT },
      );
    }
  };

  const deleteImage = (i) => {
    setFormData((prev) => {
      const updatedImages = prev.vehicle_images.filter(
        (_, index) => index !== i,
      );

      // Set error only if all images are deleted
      if (updatedImages.length === 0) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          vehicle_images: `${t("at")} ${t("least")} ${t("one")} ${t(
            "image",
          )} ${t("is")} ${t("required")}`,
        }));
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          vehicle_images: "", // Clear the error
        }));
      }

      return {
        ...prev,
        vehicle_images: updatedImages,
      };
    });

    // Revoke object URL to prevent memory leaks
    URL.revokeObjectURL(preview.imagesReview[i]);

    setPreview((prev) => ({
      ...prev,
      imagesReview: prev.imagesReview.filter((_, index) => index !== i),
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "vehicle_images") {
        if (!value || value.length === 0) {
          newErrors.vehicle_images = `${t("at")} ${t("least")} ${t("one")} ${t(
            "image",
          )} ${t("is")} ${t("required")}`;
        } else if (value.length > 10) {
          newErrors.vehicle_images = `${t("you")} ${t("can")} ${t(
            "upload",
          )} ${t("a")} ${t("maximum")} ${t("of")} 10 ${t("images")}`;
        }
      }

      if (!value)
        newErrors[key] = `${t("errors." + key)} ${t("errors.required")}`;
    });
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = `${t("invalid")} ${t("email")} ${t("format")}`;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    toast.dismiss();
    const data = multiPartData2(formData);
    try {
      let response;
      id
        ? (response = await updateVehicles({ data, id }).unwrap())
        : (response = await addVehicles(data).unwrap());

      if (response.statusCode === 200 || response.statusCode === 201) {
        setShow(true);
      }
    } catch (err) {
      const mesg = err.data?.message || "Something went wrong";
      toast.error(mesg);
    }
  };

  const handleReset = () => {
    setFormData(initialState);
    setPreview(previewInitialState);
  };

  return (
    <>
      <div className="outerBox">
        <div className="d-flex mb-3">
          <button className="page-back-btn" onClick={() => navigate(-1)}>
            ← {t("back")}
          </button>
        </div>
        <div className="dbSubHd mb-4">
          {t("vehicle")} {t("details")}
        </div>
        {isVehicleDetailLoading ? (
          // Skeleton Loading State
          <AddVehicleSkelton />
        ) : (
          // Actual Form Content
          <>
            <div className="mb-4">
              {/* Image Upload Section */}
              <ul className="addImg d-flex flex-wrap p-0">
                {preview.imagesReview && preview.imagesReview.length !== 0 ? (
                  <>
                    {preview.imagesReview.map((image, index) => (
                      <li
                        key={index}
                        className="col-12 col-sm-4 col-md-3 col-lg-2 mb-3"
                      >
                        <div className="upload d-flex align-items-center justify-content-center flex-column">
                          <div className="image-container position-relative">
                            <img
                              src={image}
                              alt={`Vehicle ${index + 1}`}
                              className="img-fluid rounded"
                              style={{
                                height: "120px",
                                objectFit: "cover",
                                width: "100%",
                              }}
                            />
                            <Button
                              className="editBtn position-absolute top-0 end-0 m-1"
                              onClick={() => deleteImage(index)}
                              size="sm"
                              variant="danger"
                            >
                              <RiDeleteBin5Line />
                            </Button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </>
                ) : (
                  ""
                )}
                {/* {preview.imagesReview.length === 0 && ( */}
                <li className="col-12 col-sm-4 col-md-3 col-lg-2">
                  <div
                    className="upload d-flex align-items-center justify-content-center flex-column p-3"
                    style={{ height: "120px" }}
                  >
                    <img src={Spacer} alt="" />
                    <div className="addBtn text-center">
                      <IoAddCircleOutline
                        className="addicon mb-2"
                        style={{ fontSize: "24px", color: "#6c757d" }}
                      />
                      <div className="small text-muted">
                        {t("add")} {t("vehicle")} {t("image")}
                      </div>
                    </div>
                    <input
                      type="file"
                      className="file"
                      onChange={fileHandler}
                      onClick={(e) => (e.target.value = null)}
                      accept=".png, .jpg, .jpeg"
                      multiple
                    />
                  </div>
                </li>
                {/* )} */}
              </ul>
              <div className="mt-2">
                {errors?.vehicle_images && (
                  <span className="text-danger small">
                    {errors?.vehicle_images}
                  </span>
                )}
              </div>
            </div>

            {/* Form Inputs */}
            <ul className="form d-flex flex-wrap p-0">
              {/* Row 1 */}
              <li className="col-12 col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">
                  {`${t("license")} ${t("plate")}`}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="formInput"
                  name="license_number"
                  value={formData.license_number}
                  onChange={handleChange}
                  placeholder={`${t("enter")} ${t("license")} ${t("plate")} ${t("number")}`}
                />
                {errors?.license_number && (
                  <span className="text-danger small">
                    {errors.license_number}
                  </span>
                )}
              </li>

              <li className="col-12 col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">
                  {t("registration")} {t("number")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="formInput"
                  name="registration_number"
                  value={formData.registration_number}
                  onChange={handleChange}
                  placeholder={`${t("enter")} ${t("registration")} ${t("number")}`}
                />
                {errors?.registration_number && (
                  <span className="text-danger small">
                    {errors.registration_number}
                  </span>
                )}
              </li>

              <li className="col-12 col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">
                  {t("year")} <span className="text-danger">*</span>
                </label>
                <select
                  className="formSelect"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                >
                  <option value="">
                    {t("select")} {t("year")}
                  </option>
                  {vehicleInfo.years?.length > 0
                    ? vehicleInfo.years?.map((item, i) => (
                        <option value={item.id} key={i}>
                          {item.name}
                        </option>
                      ))
                    : ""}
                </select>
                {errors?.year && (
                  <span className="text-danger small">{errors.year}</span>
                )}
              </li>

              <li className="col-12 col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">
                  {t("make")} <span className="text-danger">*</span>
                </label>
                <select
                  className="formSelect"
                  name="make"
                  value={formData.make}
                  onChange={handleChange}
                >
                  <option value="">
                    {t("select")} {t("make")}
                  </option>
                  {vehicleInfo.makes?.length > 0
                    ? vehicleInfo.makes?.map((item, i) => (
                        <option value={item.id} key={i}>
                          {item.name}
                        </option>
                      ))
                    : ""}
                </select>
                {errors?.make && (
                  <span className="text-danger small">{errors.make}</span>
                )}
              </li>

              {/* Row 2 */}
              <li className="col-12 col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">
                  {t("model")} <span className="text-danger">*</span>
                </label>
                <select
                  className="formSelect"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  disabled={!formData.make}
                >
                  <option value="">
                    {formData.make
                      ? `${t("select")} ${t("model")}`
                      : `${t("select")} ${t("make")} ${t("first")}`}
                  </option>
                  {vehicleInfo.models?.length > 0
                    ? vehicleInfo.models?.map((item, i) => (
                        <option value={item.id} key={i}>
                          {item.name}
                        </option>
                      ))
                    : ""}
                </select>
                {errors?.model && (
                  <span className="text-danger small">{errors.model}</span>
                )}
              </li>

              <li className="col-12 col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">
                  {t("color")} <span className="text-danger">*</span>
                </label>
                <select
                  className="formSelect"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                >
                  <option value="">
                    {t("select")} {t("color")}
                  </option>
                  {vehicleInfo.colors?.length > 0
                    ? vehicleInfo.colors?.map((item, i) => (
                        <option value={item.id} key={i}>
                          {item.name}
                        </option>
                      ))
                    : ""}
                </select>
                {errors?.color && (
                  <span className="text-danger small">{errors.color}</span>
                )}
              </li>

              <li className="col-12 col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">
                  {t("seat")} <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="formInput"
                  name="seat"
                  value={formData.seat}
                  onChange={handleChange}
                  placeholder={`${t("enter")} ${t("number")} ${t("of")} ${t("seats")}`}
                  min="1"
                />
                {errors?.seat && (
                  <span className="text-danger small">{errors.seat}</span>
                )}
              </li>

              {/* Row 3 */}
              <li className="col-12 col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">
                  {`${t("current")} ${t("mileage")}`}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="formInput"
                  name="current_mileage"
                  value={formData.current_mileage}
                  onChange={handleChange}
                  placeholder={`${t("enter")} ${t("current")} ${t("mileage")}`}
                />
                {errors?.current_mileage && (
                  <span className="text-danger small">
                    {errors.current_mileage}
                  </span>
                )}
              </li>

              <li className="col-12 col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">
                  {t("vin")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="formInput"
                  name="vin"
                  value={formData.vin}
                  onChange={handleChange}
                  placeholder={`${t("enter")} ${t("vin")} ${t("number")}`}
                />
                {errors?.vin && (
                  <span className="text-danger small">{errors?.vin}</span>
                )}
              </li>

              <li className="col-12 col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">
                  {`${t("vehicle")} ${t("type")}`}{" "}
                  <span className="text-danger">*</span>
                </label>
                <select
                  className="formSelect"
                  name="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={handleChange}
                >
                  <option value="">
                    {t("select")} {t("type")}
                  </option>
                  {vehicleInfo.types?.length > 0
                    ? vehicleInfo.types?.map((item, i) => (
                        <option value={item.id} key={i}>
                          {item.name}
                        </option>
                      ))
                    : ""}
                </select>
                {errors?.vehicle_type && (
                  <span className="text-danger small">
                    {errors?.vehicle_type}
                  </span>
                )}
              </li>

              {/* Row 4 */}
              <li className="col-12 col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">
                  {t("condition")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="formInput"
                  name="vehicle_condition"
                  value={formData.vehicle_condition}
                  onChange={handleChange}
                  placeholder={`${t("eg")} ${t("good")}, ${t("fair")}, ${t("poor")}`}
                />
                {errors?.vehicle_condition && (
                  <span className="text-danger small">
                    {errors?.vehicle_condition}
                  </span>
                )}
              </li>

              <li className="col-12 col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">
                  {`${t("tyre")} ${t("condition")}`}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="formInput"
                  name="tyre_condition"
                  value={formData.tyre_condition}
                  onChange={handleChange}
                  placeholder={`${t("eg")} ${t("new")}, ${t("good")}, ${t("needsReplacement")}`}
                />
                {errors?.tyre_condition && (
                  <span className="text-danger small">
                    {errors?.tyre_condition}
                  </span>
                )}
              </li>

              <li className="col-12 col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">
                  {`${t("vehicle")} ${t("rate")}`}{" "}
                  <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">$</span>
                  <input
                    type="number"
                    className="formInput"
                    name="fare_rate"
                    value={formData.fare_rate}
                    onChange={handleChange}
                    placeholder="0.00"
                    min="0"
                    step="1.00"
                  />
                </div>
                {errors?.fare_rate && (
                  <span className="text-danger small">{errors?.fare_rate}</span>
                )}
              </li>

              {/* Description */}
              <li className="col-12 mb-3">
                <label className="form-label fw-semibold">
                  {`${t("other")} ${t("conditions")}`}
                </label>
                <textarea
                  rows="4"
                  className="formInput"
                  placeholder={`${t("enter")} ${t("description")}`}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                ></textarea>
              </li>
            </ul>

            {/* Buttons */}
            <div className="pt-4 mt-3 border-top text-end">
              <Button
                variant="outline-secondary"
                onClick={handleReset}
                className="me-3 px-4 py-2"
              >
                {t("btns.reset")}
              </Button>
              <Button
                variant="primary"
                type="button"
                onClick={handleSubmit}
                disabled={isLoading || isUpdateLoading}
                className="px-4 py-2"
              >
                {isLoading || isUpdateLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    >
                      <span className="visually-hidden">{t("loading")}...</span>
                    </span>
                  </>
                ) : (
                  t("btns.submit")
                )}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Success Modal */}
      <Modal show={show} centered>
        <Button className="closePopup" onClick={handleClose}>
          <IoCloseSharp />
        </Button>
        <CreateModal
          onClose={handleClose}
          title={`${t("vehicle")} ${id ? t("updated") : t("added")} ${t(
            "successfully",
          )}!`}
          text={
            id
              ? `${t("vehicle")} ${t("has")} ${t("been")} ${t("updated")}.`
              : `${t("new")} ${t("vehicle")} ${t("has")} ${t("been")} ${t(
                  "added",
                )}.`
          }
        />
      </Modal>

      <style jsx>{`
        .input-group {
          flex-wrap: unset;
        }
      `}</style>
    </>
  );
};
export default AddVehicle;
